import React, { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { BookNowStatuses } from 'Douleutaras/types';
import { useAppSelector } from 'Douleutaras/redux/hooks';
import { getMilestoneStep } from 'Douleutaras/hooks/bids/milestoneSteps';
import { jobBidsSelectors } from 'Douleutaras/redux/data/jobBids/jobBidsSlice';
import { useMostAdvancedBookNowEntry } from 'Douleutaras/hooks/bids/bookNowEntries';
import { jobBidsBookNowSelectors } from 'Douleutaras/redux/data/jobBids/jobBidsBookNowSlice';
import { bidBuyerStatusSelectors } from 'Douleutaras/redux/data/jobBids/jobBidsStatusBuyerSlice';
import { BOOK_NOW_INDEXES, NEED_HELP_MILESTONES } from 'Douleutaras/constants/ccf/need-help-options';
import { humanReadableFormat } from 'Douleutaras/utilities/formatting/date.utils';

// Hardcode the first step because it's not dynamic
const firstMilestone = [{
  isPassed: true, // This milestone is always passed
  title: <Trans>Posted</Trans>,
  position: 1,
  isCurrent: undefined,
  dateUnformatted: undefined
}];

export const useCurrentMilestone = (bidId, paymentAvailable = false) => {
  const { t } = useTranslation();

  const bid = useAppSelector(state => jobBidsSelectors.selectById(state, bidId));

  const bookNowEntryId = bid?.relationships.bookNowEntry?.data?.id;
  const updateFromBuyerId = bid?.relationships.updateFromBuyer?.data?.id;

  const bookNowEntry = useAppSelector(state => jobBidsBookNowSelectors.selectById(state, bookNowEntryId));
  const bidBuyerEntry = useAppSelector(state => bidBuyerStatusSelectors.selectById(state, updateFromBuyerId));

  const noBookNowEntry = bookNowEntry === undefined;
  const bidBuyerEntryDate = bidBuyerEntry?.attributes?.jobStartDate;
  const buyerSelectedMilestone = bidBuyerEntry?.attributes.milestone as NEED_HELP_MILESTONES;

  const currentMilestone = useMostAdvancedBookNowEntry([bookNowEntryId], [updateFromBuyerId])
    .pop() ?? BOOK_NOW_INDEXES[0];

  // When to show each status
  const visibleBidPoints = useMemo(() => ({
    // Step 2
    [BookNowStatuses.MEETING_NOT_SCHEDULED]: currentMilestone.status === BookNowStatuses.MEETING_NOT_SCHEDULED,
    [BookNowStatuses.ACTIVE]: currentMilestone.status !== BookNowStatuses.MEETING_NOT_SCHEDULED,
    // Step 3: meeting statuses start
    [BookNowStatuses.QUOTE_NOT_SENT]: currentMilestone.status === BookNowStatuses.QUOTE_NOT_SENT,
    [BookNowStatuses.MEETING_SCHEDULED]: currentMilestone.status === BookNowStatuses.MEETING_SCHEDULED,
    [BookNowStatuses.MEETING_NOT_DONE]: currentMilestone.status === BookNowStatuses.MEETING_NOT_DONE,
    [BookNowStatuses.TO_FOLLOW_UP]: currentMilestone.status === BookNowStatuses.TO_FOLLOW_UP,
    [BookNowStatuses.MEETING_DONE]: currentMilestone.status !== BookNowStatuses.MEETING_SCHEDULED
    && currentMilestone.status !== BookNowStatuses.MEETING_NOT_DONE
    && currentMilestone.status !== BookNowStatuses.TO_FOLLOW_UP
    && currentMilestone.status !== BookNowStatuses.QUOTE_NOT_SENT,
    // meeting statuses end
    // Step 4: quote statuses start
    [BookNowStatuses.QUOTE_NOT_ACCEPTED]: currentMilestone.status === BookNowStatuses.QUOTE_NOT_ACCEPTED,
    [BookNowStatuses.QUOTE_CUSTOMER_THINKING]: currentMilestone.status === BookNowStatuses.QUOTE_CUSTOMER_THINKING,
    [BookNowStatuses.QUOTE_SENT]: currentMilestone.status !== BookNowStatuses.QUOTE_NOT_ACCEPTED
    && currentMilestone.status !== BookNowStatuses.QUOTE_CUSTOMER_THINKING,
    // quote statuses end
    // Step 5:
    [BookNowStatuses.QUOTE_ACCEPTED]: currentMilestone.status === BookNowStatuses.QUOTE_ACCEPTED,
    [BookNowStatuses.JOB_WILL_NOT_START]: currentMilestone.status === BookNowStatuses.JOB_WILL_NOT_START,
    [BookNowStatuses.CANNOT_BE_COMPLETED]: currentMilestone.status === BookNowStatuses.CANNOT_BE_COMPLETED,
    [BookNowStatuses.COMPLETION_PENDING]: currentMilestone.status === BookNowStatuses.COMPLETION_PENDING,
    [BookNowStatuses.JOB_STARTED]: currentMilestone.status !== BookNowStatuses.JOB_WILL_NOT_START
    && currentMilestone.status !== BookNowStatuses.CANNOT_BE_COMPLETED
    && currentMilestone.status !== BookNowStatuses.QUOTE_ACCEPTED
    && currentMilestone.status !== BookNowStatuses.COMPLETION_PENDING,
    // quote statuses end
    // Step 6:
    [BookNowStatuses.COMPLETED]: true
  }), [currentMilestone.status]);

  const restMilestonesFormatted = useMemo(() => BOOK_NOW_INDEXES
    .filter(milestone => visibleBidPoints[milestone.status])
    .map(milestone => {
      const milestoneStep = getMilestoneStep({
        bid,
        bookNowEntry,
        bidBuyerEntryDate,
        paymentAvailable
      });
      const mapBooknowToOwnAttributes = milestoneStep(milestone.status);

      return {
        isPassed:
          (currentMilestone.index > milestone.index || milestone.index === BOOK_NOW_INDEXES.length - 1) && !noBookNowEntry,
        isCurrent:
          (currentMilestone.index === milestone.index && milestone.index !== BOOK_NOW_INDEXES.length - 1),
        isNext: (currentMilestone.index < milestone.index && milestone.index !== BOOK_NOW_INDEXES.length - 1) && !noBookNowEntry,
        isComplete: currentMilestone.status === BookNowStatuses.COMPLETED,
        position: mapBooknowToOwnAttributes.stepNumber,
        title: mapBooknowToOwnAttributes.label,
        failed: mapBooknowToOwnAttributes.failed,
        date: mapBooknowToOwnAttributes.date ?
          humanReadableFormat(mapBooknowToOwnAttributes.date, t, 'history')
          : undefined,
        dateUnformatted: mapBooknowToOwnAttributes.date,
        stepInfo: mapBooknowToOwnAttributes.stepInfo,
        stepInfoCost: mapBooknowToOwnAttributes.stepInfoCost,
        moneyBackGuarantee: mapBooknowToOwnAttributes.moneyBackGuarantee
      };
    }), [visibleBidPoints]);

  const milestonesFormatted = firstMilestone.concat(restMilestonesFormatted);
  const currentStep = milestonesFormatted.find(milestone => milestone.isCurrent);

  return {
    currentStep,
    buyerSelectedMilestone,
    milestonesFormatted
  };
};
